// Google Tag Manager utility functions
import { isEmbedded, sendToParent } from './postMessageBridge';

declare global {
    interface Window {
        dataLayer: any[];
        gtag: (...args: any[]) => void;
    }
}

// Initialize data layer if it doesn't exist
if (typeof window !== 'undefined' && !window.dataLayer) {
    window.dataLayer = [];
}

/**
 * Send a custom event to GTM *and*, when running inside an iframe, forward it
 * to the parent site via the postMessage bridge.
 *
 * The bridge call is gated behind the "active" check inside sendToParent, so
 * events are only forwarded when the user is interacting with the iframe.
 * This prevents the parent's analytics from being inflated by background
 * activity (e.g. SWR revalidations) happening while the user is elsewhere.
 */
export const sendGTMEvent = (eventName: string, eventData: Record<string, any> = {}) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            ...eventData,
            timestamp: new Date().toISOString(),
        });
    }

    // Forward to parent site when the app is embedded inside an iframe.
    // We do this after the GTM push so the iframe's own analytics are never
    // blocked if postMessage fails.
    if (isEmbedded()) {
        sendToParent('analytics', { event: eventName, ...eventData });
    }
};

// Track page views
export const trackPageView = (pagePath: string, pageTitle?: string) => {
    sendGTMEvent('page_view', {
        page_path: pagePath,
        page_title: pageTitle || document.title,
    });
};

// Track user interactions
export const trackUserInteraction = (action: string, category: string, label?: string, value?: number) => {
    sendGTMEvent('user_interaction', {
        action,
        category,
        label,
        value,
    });
};

// Track form submissions
export const trackFormSubmission = (formName: string, formData?: Record<string, any>) => {
    sendGTMEvent('form_submit', {
        form_name: formName,
        form_data: formData,
    });
};

// Track file uploads
export const trackFileUpload = (fileType: string, fileSize: number, success: boolean) => {
    sendGTMEvent('file_upload', {
        file_type: fileType,
        file_size: fileSize,
        success,
    });
};

// Track API calls
export const trackAPICall = (endpoint: string, method: string, status: number, responseTime?: number) => {
    sendGTMEvent('api_call', {
        endpoint,
        method,
        status,
        response_time: responseTime,
    });
};

// Track errors
export const trackError = (errorMessage: string, errorStack?: string, errorContext?: Record<string, any>) => {
    sendGTMEvent('error', {
        error_message: errorMessage,
        error_stack: errorStack,
        error_context: errorContext,
    });
};

// Track performance metrics
export const trackPerformance = (metricName: string, value: number, rating?: string) => {
    sendGTMEvent('performance', {
        metric_name: metricName,
        value,
        rating,
    });
};

// Check if GTM is available
export const isGTMAvailable = (): boolean => {
    return typeof window !== 'undefined' && !!window.dataLayer;
};

// Get current data layer
export const getDataLayer = () => {
    return typeof window !== 'undefined' ? window.dataLayer : [];
};

// Clear data layer (useful for testing)
export const clearDataLayer = () => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer = [];
    }
};
